import React, { useEffect, useState, useRef, useCallback } from "react";
import ShareIcon from "@mui/icons-material/Share";
import SearchIcon from "@mui/icons-material/Search";
import CircularProgress from "@mui/material/CircularProgress";
import Snackbar from "@mui/material/Snackbar";
import SnackbarContent from "@mui/material/SnackbarContent";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useLanguage } from "../LanguageContext";
import { CiFilter } from "react-icons/ci";
import Header from "../Headers/Header";
import QRCodeDialog from "./QRCodeDialog";
import OpenDrawer from "../openDrawer/OpenDrawer";
import { translationManualList } from "./translationManualList";
import { useSelector } from "react-redux";
import {
  fetchCategories,
  fetchPackages,
  fetchSubCategories,
  fetchThumbnail,
  fetchPackageById,
} from "./fetch/slices";
import { AppDispatch, RootState } from "./fetch/store";
import { useAppDispatch } from "./fetch/hook";
import { cleanBase64ImageData } from "./encodeImage";
import { mapSubCategoriesToCategories } from "../Mappers/subcategoryToCategory";
import { useLocation, useNavigate } from "react-router-dom";
import { SelectChangeEvent } from "@mui/material/Select";
import { Category, SubCategory, Package, Language } from "../../Types/types";
import CategoryIcons from "./CategoryImagesFilter";

const ManualsList: React.FC = () => {
  const theme = useTheme();
  const [manualData, setManualData] = useState<Package[]>([]);
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [page, setPage] = useState(1);
  const [openQR, setOpenQR] = useState(false);
  const [selectedGuideId, setSelectedGuideId] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState<string>("ASC");
  const { language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch: AppDispatch = useAppDispatch();
  const categoryData = useSelector(
    (state: RootState) => state.data.categories.data
  ) as Category[];
  const subCategoryData = useSelector(
    (state: RootState) => state.data.subcategories.data
  ) as SubCategory[];
  const thumbnailData = useSelector(
    (state: RootState) => state.data.thumbnailUrl.data
  ) as Record<string, string>;
  const totalPackages = useSelector(
    (state: RootState) => state.data.totalPackages
  );

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isXLScreen = useMediaQuery(theme.breakpoints.up("xl"));
  const [openDrawer, setOpenDrawer] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedSubCategories, setSelectedSubCategories] = useState<string[]>(
    []
  );
  const t = translationManualList[language as Language];
  const [subCategoryToCategoryMap, setSubCategoryToCategoryMap] = useState<
    Record<string, string>
  >({});
  const [tempSearchTerm, setTempSearchTerm] = useState("");
  const [tempSelectedCategories, setTempSelectedCategories] = useState<
    string[]
  >([]);
  const [tempSelectedSubCategories, setTempSelectedSubCategories] = useState<
    string[]
  >([]);
  const [tempOrder, setTempOrder] = useState<string>("ASC");
  const observer = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    dispatch(fetchCategories(language));
    dispatch(fetchSubCategories(language));
  }, [dispatch, language]);

  const fetchManualData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await dispatch(
        fetchPackages({
          language,
          categoryIds: selectedCategories,
          subCategoryIds: selectedSubCategories,
          page: page - 1,
          count: itemsPerPage,
          searchQuery: searchTerm,
          order,
        })
      );
      if (fetchPackages.fulfilled.match(result)) {
        setManualData((prevData) => [
          ...(page === 1 ? [] : prevData),
          ...(result.payload.packages as Package[]),
        ]);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching manual data:", error);
    }
  }, [
    dispatch,
    language,
    selectedCategories,
    selectedSubCategories,
    page,
    itemsPerPage,
    searchTerm,
    order,
  ]);

  useEffect(() => {
    fetchManualData();
  }, [fetchManualData]);

  useEffect(() => {
    manualData.forEach((pkg) => {
      dispatch(fetchThumbnail({ id: pkg.id.toString() }));
    });
  }, [dispatch, manualData]);

  useEffect(() => {
    setSubCategoryToCategoryMap(mapSubCategoriesToCategories(subCategoryData));
  }, [subCategoryData]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const searchTerm = searchParams.get("search") || "";
    const itemsPerPage = parseInt(searchParams.get("itemsPerPage") || "15");
    const page = parseInt(searchParams.get("page") || "1");
    const selectedCategories = searchParams.get("categories")?.split(",") || [];
    const selectedSubCategories =
      searchParams.get("subCategories")?.split(",") || [];
    const fromDetail = searchParams.get("fromDetail") === "true";
    const order = searchParams.get("order") || "ASC";

    if (fromDetail) {
      const state = sessionStorage.getItem("manualsListState");
      if (state) {
        const { search, itemsPerPage, page, categories, subCategories, order } =
          JSON.parse(state);
        setSearchTerm(search);
        setItemsPerPage(parseInt(itemsPerPage));
        setPage(parseInt(page));
        setSelectedCategories(categories);
        setSelectedSubCategories(subCategories);
        setOrder(order);
      }
    } else {
      setSearchTerm(searchTerm);
      setItemsPerPage(itemsPerPage);
      setPage(page);
      setSelectedCategories(selectedCategories);
      setSelectedSubCategories(selectedSubCategories);
      setOrder(order);
    }
  }, [location.search]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.removeItem("manualsListState");
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  useEffect(() => {
    setPage(1);
    setManualData([]);
    setSelectedCategories([]);
    setSelectedSubCategories([]);
    setSearchTerm("");
    setOrder("ASC");
    fetchManualData();
  }, [language, categoryData]);

  const handleNavigate = async (guide: Package) => {
    try {
      sessionStorage.setItem(
        "manualsListState",
        JSON.stringify({
          search: searchTerm,
          itemsPerPage,
          page,
          categories: selectedCategories,
          subCategories: selectedSubCategories,
          order,
        })
      );

      const fetchedPackage = await dispatch(
        fetchPackageById({ id: guide.id.toString(), locale: language })
      );

      navigate(
        `/selected/detail/${
          guide.id
        }?lang=${language}&search=${searchTerm}&itemsPerPage=${itemsPerPage}&page=${page}&categories=${selectedCategories.join(
          ","
        )}&subCategories=${selectedSubCategories.join(",")}&order=${order}`
      );
    } catch (error) {
      console.error("Error fetching package by id:", error);
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = event.target.value;
    setSearchTerm(newSearchTerm);
    setPage(1);
  };

  const handleShareClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    guideId: number
  ) => {
    event.stopPropagation();
    setSelectedGuideId(guideId.toString());
    setOpenQR(true);
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(
      `${window.location.origin}/selected/detail/${selectedGuideId}?lang=${language}`
    );
    setSnackbarOpen(true);
  };

  const shortenURL = (url: string) => {
    const maxLength = 30;
    return url.length > maxLength ? `${url.slice(0, maxLength)}...` : url;
  };

  const handleCloseQR = () => {
    setOpenQR(false);
  };

  const handleCategoryUpdate = (categories: string[]) => {
    setSelectedCategories(categories);
    setTempSelectedCategories(categories);
    setPage(1);
    setManualData([]);
  };

  const handleSubCategoryUpdate = (subCategories: string[]) => {
    setTempSelectedSubCategories(subCategories);
    setPage(1);
  };

  const handleOrderChange = (event: SelectChangeEvent<string>) => {
    setTempOrder(event.target.value);
  };

  const handleOpenDrawer = () => {
    setTempSearchTerm(searchTerm);
    setTempSelectedCategories(selectedCategories);
    setTempSelectedSubCategories(selectedSubCategories);
    setTempOrder(order);
    setOpenDrawer(true);
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
  };

  const handleSearchClick = () => {
    setSearchTerm(tempSearchTerm);
    setSelectedCategories(tempSelectedCategories);
    setSelectedSubCategories(tempSelectedSubCategories);
    setOrder(tempOrder || "ASC");
    setPage(1);
    setOpenDrawer(false);
  };

  const handleClearClick = () => {
    setTempSearchTerm("");
    setTempSelectedCategories([]);
    setTempSelectedSubCategories([]);
    setTempOrder("ASC");
  };

  const handleDrawerSearchTermChange = (newSearchTerm: string) => {
    setTempSearchTerm(newSearchTerm);
  };

  const lastElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && manualData.length < totalPackages) {
          setPage((prevPage) => prevPage + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [manualData.length, totalPackages]
  );

  const filteredManualData = manualData.filter((guide) =>
    guide.label?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedGuide = filteredManualData.find(
    (guide) => guide.id === parseInt(selectedGuideId)
  );

  const selectedGuideTitle = selectedGuide?.label;

  return (
    <div className="bg-white dark:bg-darkBackground min-h-screen flex flex-col items-center">
      <div className="sticky w-full top-0 z-10">
        <Header />
      </div>

      <main className="flex-1 pt-6 pb-10 w-full mx-auto">
        <div className="bg-white dark:bg-darkBackground rounded-lg flex flex-col items-center">
          <div>
            <div className="flex mx-4">
              <div className="flex sm:justify-between items-center w-full mb-4 relative gap-1">
                <div className="relative order-1 sm:order-2 sm:flex-grow-0 flex-grow">
                  <input
                    type="text"
                    placeholder={t.search}
                    className="pl-2 pr-10 py-2 w-full border-gray-400 bg-white dark:bg-darkBackground dark:text-white text-black rounded-md outline-none border border-purple sm:flex-grow-0 flex-grow"
                    value={searchTerm}
                    onChange={handleSearchChange}
                  />
                  <SearchIcon className="absolute right-1 transform translate-y-1/3 text-gray-600 dark:text-white" />
                </div>
                <button
                  onClick={handleOpenDrawer}
                  className="rounded-md flex items-center gap-2 opacity-70 hover:opacity-100 dark:opacity-100 transition-all dark:text-white order-2 sm:order-1"
                >
                  <div className="p-3 bg-purple text-black dark:text-white rounded-md">
                    <CiFilter />
                  </div>
                  <p className="sm:block hidden">Filter</p>
                </button>
              </div>
            </div>

            {selectedCategories.length > 0 && (
              <div className="bg-lightPurple rounded-md p-2 md:flex gap-2 ml-4 mr-4 hidden">
                {selectedCategories.length > 0 &&
                  selectedCategories[0] !== "all" && (
                    <div className="text-white bg-purple p-1 rounded">
                      <span>
                        {selectedCategories
                          .map(
                            (categoryId) =>
                              categoryData.find(
                                (category) =>
                                  category.id.toString() === categoryId
                              )?.name
                          )
                          .join(", ") || t.all}
                      </span>
                    </div>
                  )}
                {selectedSubCategories.length > 0 &&
                  selectedSubCategories[0] !== "all" && (
                    <div className="text-white bg-purple p-1 px-2 rounded md:flex ">
                      <span>
                        {selectedSubCategories
                          .map(
                            (subCategoryId) =>
                              subCategoryData.find(
                                (subCategory) =>
                                  subCategory.id.toString() === subCategoryId
                              )?.name
                          )
                          .join(", ") || t.all}
                      </span>
                    </div>
                  )}
              </div>
            )}
            <CategoryIcons
              categories={categoryData}
              language={language}
              selectedCategories={selectedCategories}
              onCategorySelect={handleCategoryUpdate}
            />

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-5 gap-2 w-full p-2 relative max-w-[1600px]">
              {filteredManualData.length > 0 ? (
                filteredManualData.map((guide, index) => (
                  <div
                    key={index}
                    ref={
                      index === filteredManualData.length - 1
                        ? lastElementRef
                        : null
                    }
                    className="bg-lightPurple dark:bg-purple dark:hover:bg-darkPurple p-2 rounded-lg shadow-md flex flex-col items-start relative cursor-pointer group scale-95 hover:scale-100 transition-all hover:bg-lightPink"
                    style={{ aspectRatio: "1 / 1" }}
                    onClick={() => handleNavigate(guide)}
                  >
                    <img
                      src={
                        cleanBase64ImageData(
                          thumbnailData[guide.id.toString()]
                        ) || "/Group_125.svg"
                      }
                      alt={guide.label}
                      className="w-full h-full object-cover mb-2 rounded"
                    />
                    <h2 className="text-md text-gray-900 dark:text-white mb-1 truncate w-full">
                      {guide.label}
                    </h2>

                    <div className="absolute top-4 right-4 md:hidden group-hover:block rounded-full bg-white opacity-60 hover:opacity-100 p-2 pt-1.5">
                      <button
                        aria-label="share"
                        onClick={(event) => handleShareClick(event, guide.id)}
                      >
                        <ShareIcon />
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center my-4 col-span-full">
                  <p className="text-lg text-gray-500">
                    No data matches this category.
                  </p>
                </div>
              )}
            </div>
            {loading && (
              <div className="flex justify-center my-4 w-full">
                <CircularProgress />
              </div>
            )}
          </div>
          <OpenDrawer
            open={openDrawer}
            onClose={handleCloseDrawer}
            searchTerm={tempSearchTerm}
            selectedCategories={tempSelectedCategories}
            selectedSubCategories={tempSelectedSubCategories}
            onSearchChange={handleSearchChange}
            onCategorySelect={handleCategoryUpdate}
            onSubCategorySelect={handleSubCategoryUpdate}
            categoryData={categoryData}
            subCategoryData={subCategoryData}
            order={tempOrder || "ASC"}
            onOrderChange={handleOrderChange}
            onSearchClick={handleSearchClick}
            onClearClick={handleClearClick}
            onSearchTermChange={handleDrawerSearchTermChange}
          />
          {selectedGuideTitle && (
            <QRCodeDialog
              open={openQR}
              onClose={handleCloseQR}
              guideId={selectedGuideId}
              language={language}
              guideTitle={selectedGuideTitle}
              shortenURL={shortenURL}
              handleCopyToClipboard={handleCopyToClipboard}
            />
          )}
        </div>
      </main>

      <Snackbar
        className="font-sans"
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <SnackbarContent
          message={t.message}
          sx={{
            backgroundColor: "#ffe8e8",
            color: "white",
          }}
        />
      </Snackbar>
    </div>
  );
};

export default ManualsList;
